import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getPool, submitGuess, revealName, getPoolGuesses, deleteGuess } from '../services/api';
import type { PoolResponse, GuessDetailResponse } from '../types/api';
import axios from 'axios';
import Navigation from '../components/Navigation';
import { track, trackCreateCta } from '../services/analytics';

function PoolPage() {
  const { poolId } = useParams<{ poolId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const adminTokenFromUrl = searchParams.get('admin');
  const isAdmin = !!adminTokenFromUrl;

  const [pool, setPool] = useState<PoolResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // Guess form state
  const [playerName, setPlayerName] = useState<string>('');
  const [guessedNames, setGuessedNames] = useState<string[]>(['', '', '', '', '', '']);
  const [guessedBirthDate, setGuessedBirthDate] = useState<string>('');
  const [guessedSex, setGuessedSex] = useState<string>('');
  const [guessedBirthTime, setGuessedBirthTime] = useState<string>('');
  const [guessedWeightLbs, setGuessedWeightLbs] = useState<string>('');
  const [guessedWeightOz, setGuessedWeightOz] = useState<string>('');
  const [guessedCustomValue, setGuessedCustomValue] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);

  // Reveal form state
  const [babyName, setBabyName] = useState<string>('');
  const [birthDate, setBirthDate] = useState<string>('');
  const [sex, setSex] = useState<string>('');
  const [birthTime, setBirthTime] = useState<string>('');
  const [weightLbs, setWeightLbs] = useState<string>('');
  const [weightOz, setWeightOz] = useState<string>('');
  const [customValue, setCustomValue] = useState<string>('');
  const [revealing, setRevealing] = useState<boolean>(false);
  const [showRevealForm, setShowRevealForm] = useState<boolean>(false);

  // Copy link state
  const [copiedAdmin, setCopiedAdmin] = useState<boolean>(false);
  const [copiedShare, setCopiedShare] = useState<boolean>(false);

  // Admin guesses view state
  const [guesses, setGuesses] = useState<GuessDetailResponse[]>([]);
  const [loadingGuesses, setLoadingGuesses] = useState<boolean>(false);
  const [showGuesses, setShowGuesses] = useState<boolean>(false);
  const [deletingGuessId, setDeletingGuessId] = useState<number | null>(null);

  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);

  useEffect(() => {
    loadPool();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poolId]);

  const loadPool = async () => {
    if (!poolId) return;

    try {
      const data = await getPool(poolId);
      setPool(data);

      // If pool is revealed, redirect to results
      if (data.status === 'revealed') {
        navigate(`/results/${poolId}`);
      }
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.detail || 'Failed to load pool');
      } else {
        setError('Failed to load pool');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitGuess = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!playerName.trim()) {
      setError('Please enter your name');
      return;
    }

    // Filter out empty guesses
    const validNames = guessedNames.filter(name => name.trim() !== '');

    // If names are enabled, require at least one name
    if (pool && pool.enable_name && validNames.length === 0) {
      setError('Please enter at least one name guess');
      return;
    }

    // Show confirmation modal instead of submitting directly
    setShowConfirmModal(true);
  };

  const confirmSubmitGuess = async () => {
    if (!poolId) return;

    setShowConfirmModal(false);
    setSubmitting(true);

    // Filter out empty guesses
    const validNames = guessedNames.filter(name => name.trim() !== '');

    // Convert lbs + oz to total pounds
    let totalWeight: number | null = null;
    if (guessedWeightLbs || guessedWeightOz) {
      const lbs = guessedWeightLbs ? parseInt(guessedWeightLbs) : 0;
      const oz = guessedWeightOz ? parseInt(guessedWeightOz) : 0;
      totalWeight = lbs + (oz / 16);
    }

    try {
      await submitGuess(poolId, {
        player_name: playerName.trim(),
        guessed_names: validNames.length > 0 ? validNames.map(n => n.trim()) : [''],
        guessed_birth_date: guessedBirthDate || null,
        guessed_sex: guessedSex || null,
        guessed_birth_time: guessedBirthTime || null,
        guessed_weight: totalWeight,
        guessed_custom_value: guessedCustomValue || null,
      });
      track('Guess Submitted', { guesses: validNames.length });
      setSuccess(`Guess${validNames.length > 1 ? 'es' : ''} submitted successfully! Thanks for playing!`);
      setPlayerName('');
      setGuessedNames(['', '', '', '', '', '']);
      setGuessedBirthDate('');
      setGuessedSex('');
      setGuessedBirthTime('');
      setGuessedWeightLbs('');
      setGuessedWeightOz('');
      setGuessedCustomValue('');
      // Reload pool to update participant count
      loadPool();
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.detail || 'Failed to submit guess');
      } else {
        setError('Failed to submit guess');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleNameChange = (index: number, value: string) => {
    const newNames = [...guessedNames];
    newNames[index] = value;
    setGuessedNames(newNames);
  };

  const handleReveal = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    if (!poolId || !pool) return;

    // Only require baby name if name category is enabled
    if (pool.enable_name && !babyName.trim()) {
      setError('Please enter the baby name');
      return;
    }

    const adminToken = adminTokenFromUrl || localStorage.getItem(`admin_token_${poolId}`);
    if (!adminToken) {
      setError('Admin token not found');
      return;
    }

    setRevealing(true);

    // Convert lbs + oz to total pounds
    let totalWeight: number | null = null;
    if (weightLbs || weightOz) {
      const lbs = weightLbs ? parseInt(weightLbs) : 0;
      const oz = weightOz ? parseInt(weightOz) : 0;
      totalWeight = lbs + (oz / 16);
    }

    try {
      await revealName(poolId, {
        baby_name: babyName.trim(),
        admin_token: adminToken,
        birth_date: birthDate || null,
        sex: sex || null,
        birth_time: birthTime || null,
        weight: totalWeight,
        custom_value: customValue || null,
      });
      track('Pool Revealed', { participants: pool.participant_count });
      // Redirect to results page
      navigate(`/results/${poolId}`);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.detail || 'Failed to reveal name');
      } else {
        setError('Failed to reveal name');
      }
    } finally {
      setRevealing(false);
    }
  };

  const copyAdminLink = () => {
    const link = `${window.location.origin}/pool/${poolId}?admin=${adminTokenFromUrl}`;
    navigator.clipboard.writeText(link);
    setCopiedAdmin(true);
    setTimeout(() => setCopiedAdmin(false), 2000);
  };

  const copyShareLink = () => {
    const link = `${window.location.origin}/pool/${poolId}`;
    navigator.clipboard.writeText(link);
    track('Share Link Copied');
    setCopiedShare(true);
    setTimeout(() => setCopiedShare(false), 2000);
  };

  const loadGuesses = async () => {
    if (!poolId || !adminTokenFromUrl) return;

    setLoadingGuesses(true);
    try {
      const data = await getPoolGuesses(poolId, adminTokenFromUrl);
      setGuesses(data);
      setShowGuesses(true);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.detail || 'Failed to load guesses');
      } else {
        setError('Failed to load guesses');
      }
    } finally {
      setLoadingGuesses(false);
    }
  };

  const handleDeleteGuess = async (guessId: number) => {
    if (!poolId || !adminTokenFromUrl) return;
    if (!window.confirm('Are you sure you want to delete this submission?')) return;

    setDeletingGuessId(guessId);
    try {
      await deleteGuess(poolId, guessId, adminTokenFromUrl);
      setGuesses(guesses.filter(g => g.id !== guessId));
      loadPool(); // Update participant count
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.detail || 'Failed to delete guess');
      } else {
        setError('Failed to delete guess');
      }
    } finally {
      setDeletingGuessId(null);
    }
  };

  const formatWeight = (pounds: number | null): string => {
    if (!pounds) return 'N/A';
    const lbs = Math.floor(pounds);
    const oz = Math.round((pounds - lbs) * 16);
    return `${lbs} lbs ${oz} oz`;
  };

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading pool...</p>
        </div>
      </>
    );
  }

  if (!pool) {
    return (
      <>
        <Navigation />
        <div className="container">
          <div className="card">
            <div className="error-message">Pool not found</div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="container">
        <div className="header">
          <h1>Stork Pool</h1>
          <p>Pool created by {pool.creator_name}</p>
        </div>

      {error && <div className="card"><div className="error-message">{error}</div></div>}
      {success && <div className="card"><div className="success-message">{success}</div></div>}

      {/* After guessing: invite participant to start their own pool */}
      {success && !isAdmin && (
        <div className="card" style={{ textAlign: 'center' }}>
          <h2 style={{ marginBottom: '8px' }}>🍼 Expecting, or planning a shower?</h2>
          <p style={{ marginBottom: '16px', color: '#718096' }}>
            Start your own free pool in 30 seconds. No sign-up required.
          </p>
          <button
            onClick={() => {
              trackCreateCta('pool_after_guess');
              navigate('/');
            }}
            className="btn btn-primary btn-full"
          >
            Create My Own Pool
          </button>
        </div>
      )}

      {/* Pool Info */}
      <div className="card">
        <h2 style={{ marginBottom: '16px' }}>Pool Information</h2>
        <div className="stats">
          <div className="stat">
            <div className="stat-value">{pool.participant_count}</div>
            <div className="stat-label">Participants</div>
          </div>
          <div className="stat">
            <div className="stat-value">{pool.status === 'open' ? 'Open' : 'Closed'}</div>
            <div className="stat-label">Status</div>
          </div>
        </div>

        {pool.note && (
          <div className="info-box" style={{ marginTop: '16px', marginBottom: '16px' }}>
            <p style={{ margin: 0 }}>
              <strong>Note from {pool.creator_name}:</strong> {pool.note}
            </p>
          </div>
        )}

        {isAdmin && (
          <>
            <div style={{ marginBottom: '16px' }}>
              <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>
                Your Admin Link
                {pool && !pool.is_owner && (
                  <span> (<a href="/register" style={{ color: '#667eea', textDecoration: 'underline' }}>create an account</a> to manage this pool easily)</span>
                )}
              </label>
              <div className="copy-link-box">
                <input
                  type="text"
                  className="form-input"
                  value={`${window.location.origin}/pool/${poolId}?admin=${adminTokenFromUrl}`}
                  readOnly
                />
                <button onClick={copyAdminLink} className="btn btn-secondary">
                  {copiedAdmin ? 'Copied!' : 'Copy Admin Link'}
                </button>
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>
                Share Link (for participants)
              </label>
              <div className="copy-link-box">
                <input
                  type="text"
                  className="form-input"
                  value={`${window.location.origin}/pool/${poolId}`}
                  readOnly
                />
                <button onClick={copyShareLink} className="btn btn-secondary">
                  {copiedShare ? 'Copied!' : 'Copy Share Link'}
                </button>
              </div>
            </div>
            <div className="info-box">
              <p>
                <strong>Note:</strong> If you are the parent or are otherwise not participating,
                you don't need to submit guesses. Just share the link and reveal when ready!
              </p>
            </div>
          </>
        )}
      </div>

      {/* Guess Form */}
      {pool.status === 'open' && !success && (
        <div className="card">
          <h2 style={{ marginBottom: '24px' }}>Submit Your Guess</h2>
          <form onSubmit={handleSubmitGuess}>
            <div className="form-group">
              <label htmlFor="playerName" className="form-label">
                Your Name
              </label>
              <input
                type="text"
                id="playerName"
                className="form-input"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                placeholder="Enter your name"
                disabled={submitting}
                maxLength={40}
              />
            </div>

            {pool.enable_name && (
              <div className="form-group">
                <label className="form-label">
                  Your Guesses for Baby's Name (1-6 names)
                </label>
                {guessedNames.map((name, index) => (
                  <input
                    key={index}
                    type="text"
                    className="form-input"
                    value={name}
                    onChange={(e) => handleNameChange(index, e.target.value)}
                    placeholder={`Guess #${index + 1}${index === 0 ? ' (required)' : ' (optional)'}`}
                    disabled={submitting}
                    maxLength={40}
                    style={{ marginBottom: index < 5 ? '8px' : '0' }}
                  />
                ))}
              </div>
            )}

            {pool.enable_date && (
              <div className="form-group">
                <label htmlFor="guessedBirthDate" className="form-label">
                  Guess Birth Date{pool.due_date && ` (Due: ${new Date(pool.due_date).toLocaleDateString()})`}
                </label>
                <input
                  type="date"
                  id="guessedBirthDate"
                  className="form-input"
                  value={guessedBirthDate}
                  onChange={(e) => setGuessedBirthDate(e.target.value)}
                  disabled={submitting}
                />
              </div>
            )}

            {pool.enable_sex && (
              <div className="form-group">
                <label htmlFor="guessedSex" className="form-label">
                  Guess Baby's Sex
                </label>
                <select
                  id="guessedSex"
                  className="form-input"
                  value={guessedSex}
                  onChange={(e) => setGuessedSex(e.target.value)}
                  disabled={submitting}
                >
                  <option value="">Select...</option>
                  <option value="boy">Boy</option>
                  <option value="girl">Girl</option>
                </select>
              </div>
            )}

            {pool.enable_time && (
              <div className="form-group">
                <label htmlFor="guessedBirthTime" className="form-label">
                  Guess Birth Time
                </label>
                <input
                  type="time"
                  id="guessedBirthTime"
                  className="form-input"
                  value={guessedBirthTime}
                  onChange={(e) => setGuessedBirthTime(e.target.value)}
                  disabled={submitting}
                />
              </div>
            )}

            {pool.enable_weight && (
              <div className="form-group">
                <label className="form-label">
                  Guess Birth Weight
                </label>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <label htmlFor="guessedWeightLbs" style={{ fontSize: '0.85rem', color: '#718096', marginBottom: '4px', display: 'block' }}>
                      Pounds
                    </label>
                    <input
                      type="number"
                      id="guessedWeightLbs"
                      className="form-input"
                      value={guessedWeightLbs}
                      onChange={(e) => setGuessedWeightLbs(e.target.value)}
                      placeholder=""
                      disabled={submitting}
                      min="0"
                      max="20"
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label htmlFor="guessedWeightOz" style={{ fontSize: '0.85rem', color: '#718096', marginBottom: '4px', display: 'block' }}>
                      Ounces
                    </label>
                    <input
                      type="number"
                      id="guessedWeightOz"
                      className="form-input"
                      value={guessedWeightOz}
                      onChange={(e) => setGuessedWeightOz(e.target.value)}
                      placeholder=""
                      disabled={submitting}
                      min="0"
                      max="15"
                    />
                  </div>
                </div>
              </div>
            )}

            {pool.enable_custom && (
              <div className="form-group">
                <label htmlFor="guessedCustomValue" className="form-label">
                  Guess {pool.custom_category_name || 'Custom Category'}
                </label>
                <input
                  type="text"
                  id="guessedCustomValue"
                  className="form-input"
                  value={guessedCustomValue}
                  onChange={(e) => setGuessedCustomValue(e.target.value)}
                  placeholder={`Enter your guess for ${pool.custom_category_name || 'custom category'}`}
                  disabled={submitting}
                  maxLength={100}
                />
              </div>
            )}

            <button type="submit" className="btn btn-primary btn-full" disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Guess'}
            </button>
          </form>

          <div className="info-box" style={{ marginTop: '24px' }}>
            <p>
              <strong>Tip:</strong> {pool.enable_name && 'Submit up to 6 name guesses! Your score will be based on your best guess. '}
              All guesses remain hidden until the pool creator reveals the results.
            </p>
          </div>
        </div>
      )}

      {/* Admin Controls */}
      {isAdmin && pool.status === 'open' && (
        <div className="card">
          <h2 style={{ marginBottom: '16px' }}>Admin Controls</h2>
          <p style={{ marginBottom: '16px', color: '#718096' }}>
            When you're ready, reveal the actual results to calculate scores and show the leaderboard.
          </p>

          {/* View All Guesses Button */}
          {!showGuesses && (
            <button
              onClick={loadGuesses}
              className="btn btn-secondary btn-full"
              disabled={loadingGuesses}
              style={{ marginBottom: '16px' }}
            >
              {loadingGuesses ? 'Loading...' : `View All Guesses (${pool.participant_count})`}
            </button>
          )}

          {/* All Guesses Display */}
          {showGuesses && guesses.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ margin: 0 }}>All Guesses ({guesses.length})</h3>
                <button
                  onClick={() => setShowGuesses(false)}
                  className="btn btn-secondary"
                  style={{ padding: '4px 12px', fontSize: '0.85rem' }}
                >
                  Hide
                </button>
              </div>
              <div style={{ maxHeight: '500px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                {guesses.map((guess, index) => (
                  <div
                    key={guess.id}
                    style={{
                      padding: '16px',
                      borderBottom: index < guesses.length - 1 ? '1px solid #e2e8f0' : 'none',
                      backgroundColor: index % 2 === 0 ? '#f7fafc' : 'white'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                        {guess.player_name}
                      </div>
                      <button
                        onClick={() => handleDeleteGuess(guess.id)}
                        disabled={deletingGuessId === guess.id}
                        style={{
                          background: '#e53e3e',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '4px 8px',
                          fontSize: '0.75rem',
                          cursor: deletingGuessId === guess.id ? 'not-allowed' : 'pointer',
                          opacity: deletingGuessId === guess.id ? 0.6 : 1
                        }}
                      >
                        {deletingGuessId === guess.id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#718096', marginBottom: '8px' }}>
                      Submitted: {new Date(guess.submitted_at).toLocaleString()}
                    </div>
                    {guess.guessed_names && guess.guessed_names.length > 0 && guess.guessed_names[0] && (
                      <div style={{ marginBottom: '4px' }}>
                        <strong>Names:</strong> {guess.guessed_names.join(', ')}
                      </div>
                    )}
                    {guess.guessed_birth_date && (
                      <div style={{ marginBottom: '4px' }}>
                        <strong>Birth Date:</strong> {new Date(guess.guessed_birth_date).toLocaleDateString()}
                      </div>
                    )}
                    {guess.guessed_sex && (
                      <div style={{ marginBottom: '4px' }}>
                        <strong>Sex:</strong> {guess.guessed_sex}
                      </div>
                    )}
                    {guess.guessed_birth_time && (
                      <div style={{ marginBottom: '4px' }}>
                        <strong>Birth Time:</strong> {guess.guessed_birth_time}
                      </div>
                    )}
                    {guess.guessed_weight && (
                      <div style={{ marginBottom: '4px' }}>
                        <strong>Weight:</strong> {formatWeight(guess.guessed_weight)}
                      </div>
                    )}
                    {guess.guessed_custom_value && (
                      <div style={{ marginBottom: '4px' }}>
                        <strong>{pool.custom_category_name || 'Custom'}:</strong> {guess.guessed_custom_value}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!showRevealForm ? (
            <button
              onClick={() => setShowRevealForm(true)}
              className="btn btn-primary btn-full"
            >
              Reveal Results
            </button>
          ) : (
            <form onSubmit={handleReveal}>
              {pool.enable_name && (
                <div className="form-group">
                  <label htmlFor="babyName" className="form-label">
                    Baby's Name *
                  </label>
                  <input
                    type="text"
                    id="babyName"
                    className="form-input"
                    value={babyName}
                    onChange={(e) => setBabyName(e.target.value)}
                    placeholder="Enter the actual baby name"
                    disabled={revealing}
                    maxLength={40}
                    autoFocus
                  />
                </div>
              )}

              {pool.enable_date && (
                <div className="form-group">
                  <label htmlFor="birthDate" className="form-label">
                    Actual Birth Date
                  </label>
                  <input
                    type="date"
                    id="birthDate"
                    className="form-input"
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                    disabled={revealing}
                  />
                </div>
              )}

              {pool.enable_sex && (
                <div className="form-group">
                  <label htmlFor="sex" className="form-label">
                    Actual Sex
                  </label>
                  <select
                    id="sex"
                    className="form-input"
                    value={sex}
                    onChange={(e) => setSex(e.target.value)}
                    disabled={revealing}
                  >
                    <option value="">Select...</option>
                    <option value="boy">Boy</option>
                    <option value="girl">Girl</option>
                  </select>
                </div>
              )}

              {pool.enable_time && (
                <div className="form-group">
                  <label htmlFor="birthTime" className="form-label">
                    Actual Birth Time
                  </label>
                  <input
                    type="time"
                    id="birthTime"
                    className="form-input"
                    value={birthTime}
                    onChange={(e) => setBirthTime(e.target.value)}
                    disabled={revealing}
                  />
                </div>
              )}

              {pool.enable_weight && (
                <div className="form-group">
                  <label className="form-label">
                    Actual Birth Weight
                  </label>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <label htmlFor="weightLbs" style={{ fontSize: '0.85rem', color: '#718096', marginBottom: '4px', display: 'block' }}>
                        Pounds
                      </label>
                      <input
                        type="number"
                        id="weightLbs"
                        className="form-input"
                        value={weightLbs}
                        onChange={(e) => setWeightLbs(e.target.value)}
                        placeholder=""
                        disabled={revealing}
                        min="0"
                        max="20"
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label htmlFor="weightOz" style={{ fontSize: '0.85rem', color: '#718096', marginBottom: '4px', display: 'block' }}>
                        Ounces
                      </label>
                      <input
                        type="number"
                        id="weightOz"
                        className="form-input"
                        value={weightOz}
                        onChange={(e) => setWeightOz(e.target.value)}
                        placeholder=""
                        disabled={revealing}
                        min="0"
                        max="15"
                      />
                    </div>
                  </div>
                </div>
              )}

              {pool.enable_custom && (
                <div className="form-group">
                  <label htmlFor="customValue" className="form-label">
                    Actual {pool.custom_category_name || 'Custom Value'}
                  </label>
                  <input
                    type="text"
                    id="customValue"
                    className="form-input"
                    value={customValue}
                    onChange={(e) => setCustomValue(e.target.value)}
                    placeholder={`Enter actual ${pool.custom_category_name || 'custom value'}`}
                    disabled={revealing}
                    maxLength={40}
                  />
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="submit" className="btn btn-primary" disabled={revealing} style={{ flex: 1 }}>
                  {revealing ? 'Revealing...' : 'Confirm & Reveal'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowRevealForm(false)}
                  className="btn btn-secondary"
                  disabled={revealing}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ marginBottom: '16px', textAlign: 'center' }}>Confirm Your Submission</h2>
            <p style={{ marginBottom: '16px', color: '#718096', textAlign: 'center' }}>
              Please review your guesses before submitting:
            </p>

            <div style={{ backgroundColor: '#f7fafc', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
              <div style={{ marginBottom: '12px' }}>
                <strong>Your Name:</strong> {playerName}
              </div>

              {pool?.enable_name && (
                <div style={{ marginBottom: '12px' }}>
                  <strong>Name Guesses:</strong>{' '}
                  {guessedNames.filter(n => n.trim()).join(', ') || <span style={{ color: '#e53e3e' }}>None entered</span>}
                </div>
              )}

              {pool?.enable_date && (
                <div style={{ marginBottom: '12px' }}>
                  <strong>Birth Date:</strong>{' '}
                  {guessedBirthDate ? new Date(guessedBirthDate).toLocaleDateString() : <span style={{ color: '#e53e3e' }}>Not entered</span>}
                </div>
              )}

              {pool?.enable_sex && (
                <div style={{ marginBottom: '12px' }}>
                  <strong>Sex:</strong>{' '}
                  {guessedSex || <span style={{ color: '#e53e3e' }}>Not entered</span>}
                </div>
              )}

              {pool?.enable_time && (
                <div style={{ marginBottom: '12px' }}>
                  <strong>Birth Time:</strong>{' '}
                  {guessedBirthTime || <span style={{ color: '#e53e3e' }}>Not entered</span>}
                </div>
              )}

              {pool?.enable_weight && (
                <div style={{ marginBottom: '12px' }}>
                  <strong>Weight:</strong>{' '}
                  {guessedWeightLbs || guessedWeightOz
                    ? `${guessedWeightLbs || 0} lbs ${guessedWeightOz || 0} oz`
                    : <span style={{ color: '#e53e3e' }}>Not entered</span>}
                </div>
              )}

              {pool?.enable_custom && (
                <div style={{ marginBottom: '12px' }}>
                  <strong>{pool.custom_category_name || 'Custom'}:</strong>{' '}
                  {guessedCustomValue || <span style={{ color: '#e53e3e' }}>Not entered</span>}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={confirmSubmitGuess}
                className="btn btn-primary"
                style={{ flex: 1 }}
              >
                Submit
              </button>
              <button
                onClick={() => setShowConfirmModal(false)}
                className="btn btn-secondary"
                style={{ flex: 1 }}
              >
                Go Back & Edit
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PoolPage;
